import { BLUE, LIGHT_PURPLE } from "@/css/constants";
import { FullReview, ReviewRoundState } from "@/types/meetings";
import { formatDate } from "@/utils/datetime";

export function getReviewColor({
  canceledAt,
  completedAt,
}: {
  canceledAt?: string | null;
  completedAt?: string | null;
}) {
  return canceledAt || completedAt ? LIGHT_PURPLE : BLUE;
}

export function getRoundState(
  review: FullReview,
  rounds: NonNullable<FullReview["rounds"]>,
  roundNum: number,
): ReviewRoundState {
  const {
    acceptedAt,
    numRounds = 0,
    numRoundsComplete = 0,
    completedAt,
  } = review;

  // roundNum starts at 1, so this gets us the next round in a 0-indexed array
  const nextRound = rounds[roundNum];
  const awaitingRevision =
    (nextRound && !nextRound.revisionSentAt) ||
    (review.product?.includesFinalCheck && !review.finalCheckRevisionSentAt);

  if (review.canceledAt) {
    return "CANCELED";
  } else if (roundNum === 1 && !acceptedAt) {
    return "PENDING";
  } else if (roundNum - 1 === numRoundsComplete) {
    return "IN_PROGRESS";
  } else if (roundNum <= numRoundsComplete) {
    if (
      !completedAt &&
      roundNum === numRoundsComplete &&
      (roundNum < numRounds || review.product?.includesFinalCheck) &&
      awaitingRevision
    ) {
      return "AWAITING_REVISION";
    } else {
      return "COMPLETE";
    }
  } else {
    return "PENDING";
  }
}

function dtFmt(dt: Date) {
  return formatDate(dt, "MMM do h:mma z");
}
const REVIEW_STATUS: Record<
  "COACH" | "CUSTOMER",
  Record<string, ((dt: Date) => string) | string>
> = {
  COACH: {
    PENDING: (dt) => `Please respond by ${dtFmt(dt)}`,
    CANCELED: (dt) => `Canceled ${dtFmt(dt)}`,
    IN_PROGRESS: (dt) => `Please complete review by ${dtFmt(dt)}`,
    AWAITING_REVISION: "Waiting for customer to provide revision",
    COMPLETED: (dt) => `Completed ${dtFmt(dt)}`,
  },
  CUSTOMER: {
    PENDING: "Waiting for coach to accept",
    CANCELED: (dt) => `Canceled ${dtFmt(dt)}`,
    IN_PROGRESS: "Waiting for coach to provide review",
    AWAITING_REVISION: (dt) => `Please send revision by ${dtFmt(dt)}`,
    COMPLETED: (dt) => `Completed ${dtFmt(dt)}`,
  },
};

export function getReviewAction(
  state: string,
  isCoach: boolean,
  nextDate?: string | null,
) {
  const actor = isCoach ? "COACH" : "CUSTOMER";
  const getter = REVIEW_STATUS[actor][state];
  if (typeof getter === "function") {
    return getter(new Date(nextDate || ""));
  }
  return getter;
}
